import type React from "react"
import { Table, Tag, Typography, Tooltip, type TablePaginationConfig, type TableProps } from "antd"
import { UserCellLink } from "@/components/UserCellLink"
import { DeviceBadge } from "@/components/DeviceBadge"
import type { AiLogItem } from "@/services/log/aiLogApi"
import { getAiActionTypeInfo, formatUsdAndVnd } from "@/constants/ai"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import dayjs from "dayjs"

const { Text } = Typography

interface AiLogTableProps {
  data: AiLogItem[]
  loading: boolean
  onViewDetail: (record: AiLogItem) => void
  pagination?: TableProps["pagination"]
}

export const AiLogTable: React.FC<AiLogTableProps> = ({ data, loading, onViewDetail, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<AiLogItem>["columns"] = [
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      fixed: "left",
      width: 120,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm:ss")}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "is_success",
      key: "is_success",
      width: 110,
      align: "center",
      render: (val: any, record: AiLogItem) => {
        const isSuccess = val === 1 || val === true || val === undefined
        if (!isSuccess) {
          return (
            <Tooltip title={record.error_message || "Gọi AI thất bại"}>
              <Tag color="error" className="cursor-pointer font-medium">
                Thất bại
              </Tag>
            </Tooltip>
          )
        }
        return <Tag color="success">Thành công</Tag>
      },
    },
    {
      title: "Người dùng",
      key: "user",
      width: 200,
      fixed: "left",
      render: (_, record: AiLogItem) => (
        <UserCellLink
          user={record.user}
          userId={record.user_id}
          fallback={<Tag color="default">Khách vãng lai</Tag>}
        />
      ),
    },
    {
      title: "Tác vụ & Model",
      key: "task_model",
      width: 210,
      render: (_: any, record: AiLogItem) => {
        const info = getAiActionTypeInfo(record.action_type)
        return (
          <div className="flex flex-col items-start gap-1">
            <Tooltip title={info.description}>
              <Tag
                color={info.color}
                className="m-0 h-auto cursor-default whitespace-normal break-words py-0.5 font-medium leading-snug"
              >
                {info.title}
              </Tag>
            </Tooltip>
            <span className="pl-0.5 font-mono text-[11px] text-gray-500">{record.model}</span>
          </div>
        )
      },
    },
    {
      title: "Tokens (In / Out)",
      key: "tokens",
      width: 140,
      align: "center",
      render: (_: any, record: AiLogItem) => {
        const total = (Number(record.token_input) || 0) + (Number(record.token_output) || 0)
        return (
          <div className="flex flex-col font-mono text-xs">
            <span>
              {(record.token_input || 0).toLocaleString()} / {(record.token_output || 0).toLocaleString()}
            </span>
            <Text type="secondary" className="text-[11px]">
              Tổng: {total.toLocaleString()}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Chi phí",
      dataIndex: "cost",
      key: "cost",
      width: 140,
      align: "center",
      render: (cost: number | string) => {
        const costInfo = formatUsdAndVnd(cost)
        return (
          <div className="flex flex-col font-mono text-xs">
            <span className="font-semibold text-emerald-600">{costInfo.usd}</span>
            <Text type="secondary" className="text-[11px]">
              ≈ {costInfo.vnd}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Độ trễ",
      dataIndex: "latency_ms",
      key: "latency_ms",
      width: 100,
      align: "center",
      render: (ms: number) => {
        if (!ms) return <Text type="secondary">—</Text>
        const isHigh = ms >= 5000
        const formatted = ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
        return (
          <span className={`font-mono text-xs ${isHigh ? "font-bold text-amber-600" : "text-gray-700"}`}>
            {formatted}
          </span>
        )
      },
    },
    {
      title: "Thiết bị",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 130,
      align: "center",
      render: (ua: string | null | undefined, record: AiLogItem) => (
        <DeviceBadge userAgent={ua} ipAddress={record.ip_address} />
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<AiLogItem>
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        onRow={(record) => ({
          onClick: () => onViewDetail(record),
          className: "cursor-pointer hover:bg-blue-50/40 transition-colors",
        })}
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

AiLogTable.displayName = "AiLogTable"

export default AiLogTable
